import { useNodeTree } from "@darksnow-ui/node-tree-headless";
import { useEffect, useRef } from "react";
import {
  FolderPlus,
  FilePlus,
  Trash2,
  Edit2,
  Copy,
  Scissors,
  Clipboard,
  FolderOpen,
  FolderClosed,
} from "lucide-react";

export interface ContextMenuProps {
  x: number;
  y: number;
  nodeId: string;
  containerElementRef?: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  className?: string;
  onAction?: (action: string, nodeId: string) => void;
}

export function ContextMenu({
  x,
  y,
  nodeId,
  containerElementRef,
  onClose,
  className,
  onAction,
}: ContextMenuProps) {
  const { services, controller } = useNodeTree();
  const menuRef = useRef<HTMLDivElement>(null);
  const nodes = services.store.getState().nodes;
  const isRoot = nodeId === "root" || !nodeId;
  const node = isRoot ? null : nodes.get(nodeId);
  const isExpanded = !isRoot && services.store.getState().expandedNodes.has(nodeId);

  // Focus management
  useEffect(() => {
    // Focus the menu when it opens
    menuRef.current?.focus();

    // Return focus to tree when menu closes
    return () => {
      containerElementRef?.current?.focus();
    };
  }, [containerElementRef]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleAction = async (
    action: () => void | Promise<void>,
    actionName?: string,
  ) => {
    await action();
    if (actionName && onAction) {
      onAction(actionName, nodeId);
    }
    onClose();
  };

  // Menu específico para root
  if (isRoot) {
    return (
      <div
        ref={menuRef}
        className={className || "context-menu"}
        style={{ left: x, top: y }}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <button
          className="context-menu-item"
          onClick={() =>
            handleAction(async () => {
              const name = prompt("New folder name:");
              if (name) {
                await controller.createNode(undefined, name); // undefined = root
              }
            }, "create-folder")
          }
        >
          <FolderPlus className="w-4 h-4" />
          <span>New Folder</span>
        </button>
        <button
          className="context-menu-item"
          onClick={() =>
            handleAction(async () => {
              const name = prompt("New file name:");
              if (name) {
                await controller.createNode(undefined, name); // undefined = root
              }
            }, "create-file")
          }
        >
          <FilePlus className="w-4 h-4" />
          <span>New File</span>
        </button>
        {services.store.getState().clipboardNodes?.length > 0 && (
          <>
            <div className="context-menu-divider" />
            <button
              className="context-menu-item"
              onClick={() =>
                handleAction(async () => {
                  await controller.pasteNodes(undefined); // undefined = root
                }, "paste")
              }
            >
              <Clipboard className="w-4 h-4" />
              <span>Paste</span>
            </button>
          </>
        )}
      </div>
    );
  }

  // Se não é root e não tem node, retorna null
  if (!node) return null;

  return (
    <div
      ref={menuRef}
      className={className || "context-menu"}
      style={{ left: x, top: y }}
      onClick={(e) => e.stopPropagation()}
      tabIndex={-1}
    >
      {/* Expand/Collapse for folders */}
      {node.expandable && (
        <>
          <button
            className="context-menu-item"
            onClick={() =>
              handleAction(
                () => {
                  if (isExpanded) {
                    services.navigationService.collapseNode(nodeId);
                  } else {
                    services.navigationService.expandNode(nodeId);
                  }
                },
                isExpanded ? "collapse" : "expand",
              )
            }
          >
            {isExpanded ? (
              <>
                <FolderClosed className="w-4 h-4" />
                <span>Collapse</span>
              </>
            ) : (
              <>
                <FolderOpen className="w-4 h-4" />
                <span>Expand</span>
              </>
            )}
          </button>
          <div className="context-menu-divider" />
        </>
      )}

      {/* Create actions for folders */}
      {node.expandable && (
        <>
          <button
            className="context-menu-item"
            onClick={() =>
              handleAction(async () => {
                const name = prompt("New folder name:");
                if (name) {
                  await controller.createNode(nodeId, name);
                }
              }, "create-folder")
            }
          >
            <FolderPlus className="w-4 h-4" />
            <span>New Folder</span>
          </button>
          <button
            className="context-menu-item"
            onClick={() =>
              handleAction(async () => {
                const name = prompt("New file name:");
                if (name) {
                  await controller.createNode(nodeId, name);
                }
              }, "create-file")
            }
          >
            <FilePlus className="w-4 h-4" />
            <span>New File</span>
          </button>
          <div className="context-menu-divider" />
        </>
      )}

      {/* Common actions */}
      <button
        className="context-menu-item"
        onClick={() =>
          handleAction(() => {
            controller.copyNodes([nodeId]);
          }, "copy")
        }
      >
        <Copy className="w-4 h-4" />
        <span>Copy</span>
      </button>
      <button
        className="context-menu-item"
        onClick={() =>
          handleAction(() => {
            controller.cutNodes([nodeId]);
          }, "cut")
        }
      >
        <Scissors className="w-4 h-4" />
        <span>Cut</span>
      </button>
      {node.expandable && (
        <button
          className="context-menu-item"
          onClick={() =>
            handleAction(async () => {
              await controller.pasteNodes(nodeId);
            }, "paste")
          }
        >
          <Clipboard className="w-4 h-4" />
          <span>Paste</span>
        </button>
      )}

      <div className="context-menu-divider" />

      <button
        className="context-menu-item"
        onClick={() =>
          handleAction(async () => {
            await controller.renameNode(nodeId);
          }, "rename")
        }
      >
        <Edit2 className="w-4 h-4" />
        <span>Rename</span>
      </button>

      <button
        className="context-menu-item context-menu-item-danger"
        onClick={() =>
          handleAction(async () => {
            await controller.deleteNodes([nodeId]);
          }, "delete")
        }
      >
        <Trash2 className="w-4 h-4" />
        <span>Delete</span>
      </button>
    </div>
  );
}
